import React, { useEffect, useRef, useState } from "react";
import { GoogleMap, Marker } from "@react-google-maps/api";
import { useGoogleMapsLoader } from "@/hooks/useGoogleMapsLoader";
import { CommonSearchInput } from "@/components/ui/common-search-input";

const mapContainerStyle = {
  width: "100%",
  height: "320px",
};

const GoogleMapSelector = ({ onLocationSelect, initialLat, initialLng, title }) => {
  const mapRef = useRef(null);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [service, setService] = useState(null);
  const [isMapLoaded, setIsMapLoaded] = useState(false);

  const [position, setPosition] = useState({
    lat: initialLat || 22.5937,
    lng: initialLng || 82.9629,
  });

  const [markerPosition, setMarkerPosition] = useState({
    lat: initialLat || 22.5937,
    lng: initialLng || 82.9629,
  });


  const { isLoaded, loadError } = useGoogleMapsLoader();

  useEffect(() => {
    if (initialLat && initialLng) {
      const newPosition = { lat: parseFloat(initialLat), lng: parseFloat(initialLng) };
      setPosition(newPosition);
      setMarkerPosition(newPosition);
    }
  }, [initialLat, initialLng]);

  useEffect(() => {
    if (isLoaded && !service && window.google && window.google.maps) {
      try {
        const placesService = new window.google.maps.places.AutocompleteService();
        setService(placesService);
      } catch (error) {
        console.warn("Places service not available:", error);
      }
    }
  }, [isLoaded, service]);

  const handleMapLoad = (map) => {
    mapRef.current = map;
    setIsMapLoaded(true);

    if (map && position) {
      map.panTo(position);
    }
  };

  const handleSearch = (value) => {
    setQuery(value);
    if (!value.trim() || !service) {
      setResults([]);
      return;
    }

    try {
      service.getPlacePredictions({ input: value }, (predictions) => {
        setResults(predictions || []);
      });
    } catch (error) {
      console.warn("Search error:", error);
      setResults([]);
    }
  };

  const handleResultClick = (placeId, description) => {
    if (!mapRef.current || !isMapLoaded) return;

    try {
      const placesService = new window.google.maps.places.PlacesService(mapRef.current);

      placesService.getDetails({ placeId }, (place, status) => {
        if (status === window.google.maps.places.PlacesServiceStatus.OK && place.geometry) {
          const location = place.geometry.location;
          const newPosition = { lat: location.lat(), lng: location.lng() };

          setPosition(newPosition);
          setMarkerPosition(newPosition);
          onLocationSelect(newPosition);
          setQuery(description);
          setResults([]);

          if (mapRef.current) {
            mapRef.current.panTo(newPosition);
            mapRef.current.setZoom(15);
          }
        }
      });
    } catch (error) {
      console.warn("Place details error:", error);
    }
  };

  const handleMapClick = (e) => {
    if (!isMapLoaded) return;

    try {
      const lat = e.latLng.lat();
      const lng = e.latLng.lng();
      const newPosition = { lat, lng };

      setMarkerPosition(newPosition);
      setPosition(newPosition);
      onLocationSelect(newPosition);
    } catch (error) {
      console.warn("Map click error:", error);
    }
  };

  const handleMarkerDragEnd = (e) => {
    if (!isMapLoaded) return;

    try {
      const lat = e.latLng.lat();
      const lng = e.latLng.lng();
      const newPosition = { lat, lng };

      setMarkerPosition(newPosition);
      setPosition(newPosition);
      onLocationSelect(newPosition);
    } catch (error) {
      console.warn("Marker drag error:", error);
    }
  };

  const handleMapUnmount = () => {
    mapRef.current = null;
    setIsMapLoaded(false);
  };

  if (loadError) {
    return <div className="text-destructive">Map cannot be loaded right now, sorry.</div>;
  }

  if (!isLoaded) {
    return <div className="flex justify-center items-center h-40">Loading map...</div>;
  }

  return (
    <div className="w-full rounded-lg overflow-hidden border border-border bg-background">
      {title && (
        <div className="p-3 bg-background border-b border-border text-sm font-medium text-foreground">
          {title}
        </div>
      )}

      <div className="p-3 bg-background relative">
        <CommonSearchInput
          placeholder="Search places..."
          value={query}
          onChange={(e) => handleSearch(e.target.value)}
        />

        {results.length > 0 && (
          <div className="absolute left-0 right-0 mt-1 bg-background border border-border rounded-md shadow z-[9999] max-h-60 overflow-y-auto">
            {results.map((item) => (
              <div
                key={item.place_id}
                onClick={() => handleResultClick(item.place_id, item.description)}
                className="px-3 py-2 text-sm cursor-pointer hover:bg-accent hover:text-accent-foreground"
              >
                {item.description}
              </div>
            ))}
          </div>
        )}
      </div>

      <GoogleMap
        mapContainerStyle={mapContainerStyle}
        center={position}
        zoom={10}
        onClick={handleMapClick}
        onLoad={handleMapLoad}
        onUnmount={handleMapUnmount}
      >
        {isMapLoaded && (
          <Marker position={markerPosition} draggable={true} onDragEnd={handleMarkerDragEnd} />
        )}
      </GoogleMap>

      <div className="p-3 bg-background border-t border-border text-sm text-muted-foreground">
        Click on the map, drag the marker, or search for a place
      </div>
      <div className="p-3 bg-background border-t border-border text-sm text-foreground">
        Selected: Lat {position.lat.toFixed(6)}, Lng {position.lng.toFixed(6)}
      </div>
    </div>
  );
};

export default GoogleMapSelector;